import ko from "knockout";
import internalComponentFactory from "flow/experimental/internalComponentFactory";
import MyFlow from "flow/MyFlow";
import { Dictionary } from "flow/api/types";
import Component from "flow/Component";
import bind from "bind-decorator";
import { IComponentConfig } from "./embeddedComponent";
import { ICommand } from "flow/interfaces/ICommand";

export type StackPopCallback = (arg?: any) => void;

export type StackAcceptorCallback = (arg: any) => boolean;

export default class Stack {
    myFlow = this.rootMyFlow;
    private stack = ko.observableArray<IComponentConfig<any>>([]);
    components = this.stack;

    isEmpty = ko.pureComputed<boolean>(() => {
        return this.stack().length === 0;
    });

    length = ko.pureComputed<number>(() => {
        return this.stack().length;
    });

    peek = ko.pureComputed<IComponentConfig<any>>(() => {
        if (this.stack().length > 0) {
            return this.stack()[this.stack().length - 1];
        }
        return undefined;
    });

    constructor(private readonly rootMyFlow: MyFlow) {}

    clear(): void {
        this.rootMyFlow.clearLocalNotification();
        while (!this.isEmpty()) {
            this.stack.pop();
        }
    }

    push(
        component: Component,
        viewContext: Dictionary,
        alreadyActivated?: boolean,
        popCallback?: StackPopCallback
    ): void {
        const config = {
            component: component,
            viewContext: viewContext,
            skipActivation: alreadyActivated,
            popCallback: popCallback ? popCallback : function () {}
        };

        if (!component.manifest?.isEmbedded) {
            this.myFlow.warn("Stack.push: component is not flagged as embedded");
        }

        component.load(); // Small speedup, begin loading earlier

        this.stack.push(config);
    }

    pushById(id: string, viewContext: Dictionary, popCallback?: StackPopCallback): void {
        const component = internalComponentFactory.createComponentFromId(id, this.rootMyFlow);
        this.push(component, viewContext, false, popCallback);
    }

    getStackPropertyFromTop(propertyName: string, defaultValue: any): any {
        if (this.stack().length === 0) {
            return defaultValue;
        }

        const top = this.peek();
        if (top && top.component.viewModel()) {
            const val = top.component.viewModel()[propertyName];
            return val ? val : defaultValue;
        }

        return defaultValue;
    }

    getStackPropertyFirstAcceptable(propertyName: string, defaultValue: any, acceptor: StackAcceptorCallback): any {
        if (this.stack().length === 0) {
            return defaultValue;
        }

        for (let index = this.stack().length - 1; index >= 0; --index) {
            const component = this.stack()[index].component;
            if (!component.viewModel()) {
                continue;
            }

            const val = component.viewModel()[propertyName];
            if (acceptor(val)) {
                return val;
            }
        }

        return defaultValue;
    }

    pop(optionalArg?: any): void {
        this.rootMyFlow.clearLocalNotification();

        const top = this.stack.pop();
        if (top) {
            top.popCallback(optionalArg);
        }
    }

    replaceTop(component: Component, viewContext: Dictionary, popCallback: StackPopCallback): void {
        this.rootMyFlow.clearLocalNotification();

        const config = {
            component: component,
            viewContext: viewContext,
            popCallback: popCallback ? popCallback : function () {}
        };

        component.load(); // Small speedup, begin loading earlier

        const top = this.stack.splice(this.stack().length - 1, 1, config)[0];
        top.popCallback();
    }

    leave(count: number): void {
        this.rootMyFlow.clearLocalNotification();

        const len = this.stack().length;
        if (len > count) {
            this.stack.splice(count, len - count);
        }
    }

    @bind
    canLeave(): string | boolean {
        const top = this.peek();
        if (top) {
            const result = top.component.canLeave();
            // Compatibility workaround for bad pattern.
            // Output of view model canLeave is processed by Component.
            // strings are forwarded as is, true becomes undefined and false becomes a standardized string
            // Here we want to emulate view model return value, so undefined (canLeave not existing on vm)
            // becomes true. Other results are forwarded as is.
            if (result !== undefined) {
                return result;
            }
        }
        return true;
    }

    commands = ko.pureComputed<ICommand[]>(() => {
        return ko.utils.unwrapObservable(this.getStackPropertyFromTop("commands", []));
    });

    isDirty = ko.pureComputed<boolean>(() => {
        const top = this.peek();
        if (top) {
            return top.component.isDirty();
        }
        return false;
    });

    inEdit = ko.pureComputed<boolean>(() => {
        const top = this.peek();
        if (top) {
            return top.component.inEdit();
        }
        return false;
    });

    title = ko.pureComputed<string>(() => {
        return ko.utils.unwrapObservable(
            this.getStackPropertyFirstAcceptable(
                "componentTitle",
                this.rootMyFlow.settings.component.originalTitle(),
                function acceptor(val: string) {
                    return val !== undefined;
                }
            )
        );
    });

    footerText = ko.pureComputed<string>(() => {
        return ko.utils.unwrapObservable(
            this.getStackPropertyFirstAcceptable("footerText", undefined, function acceptor(val: string) {
                return val !== undefined;
            })
        );
    });

    counter = ko.pureComputed<number>(() => {
        return ko.utils.unwrapObservable(
            this.getStackPropertyFirstAcceptable("counter", undefined, function acceptor(val: string) {
                return val !== undefined;
            })
        );
    });

    hint = ko.pureComputed<any>(() => {
        return ko.utils.unwrapObservable(
            this.getStackPropertyFirstAcceptable("hint", undefined, function acceptor(val: any) {
                return val !== undefined;
            })
        );
    });

    @bind
    onContainerSizeChanged(): void {
        this.stack().forEach(config => {
            config.component.onContainerSizeChanged();
        });
    }

    activateByIdBesideThisContainer(id: string, viewContext: Dictionary): void {
        this.rootMyFlow.activateByIdBesideThisContainer(id, viewContext);
    }
}
